import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Phone } from 'lucide-react';
import { useBrandingSettings } from '@/contexts/AppSettingsContext';
import { useCustomSiteBackdrop } from '@/hooks/useCustomSiteBackdrop';
import { cn, phoneToTelHref, phoneToWhatsAppHref } from '@/lib/utils';
import { isWebReadyTenant, WEBREADY_WHATSAPP_GDPR_MESSAGE } from '@/lib/webreadyLegal';
import { useTranslation } from 'react-i18next';

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

/** Înălțimea zonei de tap (fără safe-area) */
const BAR_HEIGHT_PX = 60;

const linkBase =
  'flex flex-1 items-center justify-center gap-2.5 text-[15px] font-medium tracking-[-0.01em] transition-colors active:opacity-80 [touch-action:manipulation]';

/** Padding so scrollable content clears the fixed bar (mobile only) */
export const MOBILE_CONTACT_BAR_OFFSET =
  `max-md:pb-[calc(${BAR_HEIGHT_PX}px+env(safe-area-inset-bottom,0px))]`;

export default function MobileContactBar() {
  const { contactPhone } = useBrandingSettings();
  const { isActive: overVideo } = useCustomSiteBackdrop();
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!contactPhone?.trim() || !mounted) {
    return null;
  }

  const isWebReady = isWebReadyTenant();
  const callLabel = isWebReady ? 'Solicită un audit gratuit' : t('public.contact.call', 'Sună');
  const whatsappLabel = t('public.contact.whatsapp', 'WhatsApp');

  const bar = (
    <nav
      data-no-theme-colors
      data-mobile-contact-bar
      className="fixed inset-x-0 bottom-0 z-[90] md:hidden pointer-events-auto"
      aria-label={t('public.contact.quickContact', 'Contact rapid')}
    >
      <div
        className={cn(
          'border-t',
          overVideo
            ? 'custom-liquid-glass border-white/15'
            : 'border-black/[0.08] bg-white/90 backdrop-blur-2xl supports-[backdrop-filter]:bg-white/80'
        )}
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div
          className="grid grid-cols-[1.4fr_1px_1fr] items-stretch"
          style={{ minHeight: `${BAR_HEIGHT_PX}px` }}
        >
          {isWebReady ? (
            <a
              href={phoneToTelHref(contactPhone)}
              className={cn(
                linkBase,
                overVideo ? 'text-white' : 'text-[#1d1d1f]',
                'px-2 text-[12.5px] sm:text-[13px] font-semibold tracking-tight text-center'
              )}
              aria-label={`${callLabel}: ${contactPhone}`}
            >
              <Phone className="h-5 w-5 shrink-0" strokeWidth={1.75} aria-hidden />
              <span className="truncate">{callLabel}</span>
            </a>
          ) : (
            <a
              href={phoneToTelHref(contactPhone)}
              className={cn(linkBase, overVideo ? 'text-white' : 'text-[#1d1d1f]')}
              aria-label={`${callLabel}: ${contactPhone}`}
            >
              <Phone className="h-6 w-6 shrink-0" strokeWidth={1.75} aria-hidden />
              <span>{callLabel}</span>
            </a>
          )}

          <div
            className={cn('w-px self-stretch', overVideo ? 'bg-white/15' : 'bg-black/[0.08]')}
            aria-hidden
          />

          <a
            href={phoneToWhatsAppHref(
              contactPhone,
              isWebReadyTenant() ? { prefillMessage: WEBREADY_WHATSAPP_GDPR_MESSAGE } : undefined
            )}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(linkBase, overVideo ? 'text-white' : 'text-[#1d1d1f]')}
            aria-label={whatsappLabel}
          >
            <WhatsAppIcon
              className={cn('h-6 w-6 shrink-0', overVideo ? 'text-white' : 'text-[#128C7E]')}
            />
            <span>{whatsappLabel}</span>
          </a>
        </div>
      </div>
    </nav>
  );

  return createPortal(bar, document.body);
}
